import React, { useCallback, useState, useEffect, Fragment } from 'react';
import TitleAndDescription from '../../common/description-component';
import TextInput from '../../topics/components/TextInput';
import SpacerVertical from '../../common/spacer-vertical';
import { SortableReaction, SortableReactionsContainer } from './draggableList';
import SelectInput from '../../common/inputs/select-input';
import { Theme, ImplementationMethod, Reaction } from './types';
import { DEFAULT_TITLE } from './consts';
import ImplementationTitle from './ImplementationTitle';
import ManualEmbedCode from './ManualEmbedCode';
import RemoveEmbedCodeMessage from './RemoveEmbedCodeMessage';
import { usePrevious } from '../../../hooks/usePrevious';
import ThemeStatusMessage from './ThemeStatusMessage';

const IMPLEMENTATIONS_OPTIONS: { value: ImplementationMethod; text: string }[] = [
  {
    value: 'keywords',
    text: 'Default (Related Keyword)'
  },
  {
    value: 'manual',
    text: 'Manual Code Integration (Theme)'
  }
];

const getInitialLocalTheme = selectedTheme => ({
  ...selectedTheme,
  reactions: selectedTheme.reactions.map(reaction => Object.assign({}, reaction))
});

const getActiveReactionsCount = (reactions: Reaction[]) =>
  reactions.reduce((count, reaction) => {
    return reaction.is_active ? count + 1 : count;
  }, 0);

const KeywordsTitle = () => (
  <div>
    <div> Keywords </div>
    <div className="reactions-keywords-sub-header">
      Please use a comma to seperate multiple keywords (min 1 keyword)
    </div>
  </div>
);

interface Props {
  spotId: string;
  minReactions: number;
  maxReactions: number;
  selectedTheme: Theme;
  isDefault: boolean;
  themeChanged: (themeId: string, newProps: Theme) => void;
  activateTheme: () => void;
  onClose: any;
  getIsThemeNameTaken: (themeName: string, uuid: string | null) => boolean;
}

const ThemeModal: React.FC<Props> = ({
  spotId,
  minReactions,
  maxReactions,
  selectedTheme,
  isDefault = false,
  themeChanged,
  activateTheme,
  onClose,
  getIsThemeNameTaken
}) => {
  const [triedToSubmit, setTriedToSubmit] = useState(false);
  const [localTheme, setLocalTheme] = useState<Theme>(getInitialLocalTheme(selectedTheme));
  const prevImplementationMethod = usePrevious(localTheme.implementation_method);
  const [showRemoveCode, setShowRemoveCode] = useState(false);
  const {
    name,
    title,
    implementation_method: implementationMethod,
    keywords,
    reactions
  } = localTheme;
  const isThemeNameTaken = getIsThemeNameTaken(name, selectedTheme.uuid);
  const isNameEmpty = name?.trim() === '';
  const showNameErrorMessage = (isNameEmpty || isThemeNameTaken) && triedToSubmit;
  const isKeywordsEmpty = keywords?.trim() === '';
  const showKeywordsErrorMessage = isKeywordsEmpty && triedToSubmit;
  const isTitleEmpty = title?.trim() === '';
  const showTitleErrorMessage = isTitleEmpty && triedToSubmit;
  const isActiveReactionsCountInvalid = getActiveReactionsCount(reactions) < minReactions;
  const showActiveReactionsCountErrorMessage = isActiveReactionsCountInvalid && triedToSubmit;

  useEffect(() => {
    const nextShowRemoveCode =
      implementationMethod === 'keywords' && prevImplementationMethod === 'manual';
    setShowRemoveCode(nextShowRemoveCode);
  }, [implementationMethod]);

  const localThemeChanged = (newThemeSettings: Partial<Theme>) =>
    setLocalTheme(prevLocalTheme => ({ ...prevLocalTheme, ...newThemeSettings }));

  const reactionSettingsChanged = useCallback(
    (reactionId, newReactionSettingsExtend) => {
      const updatedReactions = (reactions || []).map(reaction => {
        if (reaction.id === reactionId) {
          return Object.assign({}, reaction, newReactionSettingsExtend);
        }
        return reaction;
      });
      localThemeChanged({ reactions: updatedReactions });
    },
    [reactions]
  );

  const reactionOrderChanged = useCallback(
    ({ oldIndex, newIndex }) => {
      // move reaction from old to new index and update pos properties
      const updatedReactions = [...reactions];
      const itemToMove = updatedReactions[oldIndex];
      updatedReactions.splice(oldIndex, 1);
      updatedReactions.splice(newIndex, 0, itemToMove);
      updatedReactions.forEach((item, index) => {
        item.pos = index;
      });

      localThemeChanged({ reactions: updatedReactions });
    },
    [reactions]
  );

  const isValidLocalTheme = () => {
    if (isDefault) {
      return !isActiveReactionsCountInvalid;
    }

    const fieldsToCheck = [
      isThemeNameTaken,
      isNameEmpty,
      isTitleEmpty,
      isActiveReactionsCountInvalid
    ];

    if (implementationMethod === 'keywords') {
      fieldsToCheck.push(isKeywordsEmpty);
    }

    return fieldsToCheck.every(isEmpty => isEmpty === false);
  };

  const onSave = () => {
    if (!isValidLocalTheme()) {
      if (!triedToSubmit) {
        setTriedToSubmit(true);
      }
      return;
    }

    if (localTheme.uuid === null) {
      localTheme.isNewTheme = true;
      localTheme.uuid = `--temp-${localTheme.name}`;
    }

    // resetting
    localTheme.reactions = localTheme.reactions.map(r => ({ ...r, name: r.name || null }));

    themeChanged(localTheme.uuid, localTheme);
    onClose();
  };

  return (
    <div className="reactions-theme-modal-wrapper">
      <TitleAndDescription title="Reactions Theme" />

      <SpacerVertical size="20px" />

      <ThemeStatusMessage
        themeEnabled={selectedTheme.enabled}
        activateTheme={() => {
          activateTheme();
          localThemeChanged({ enabled: true });
        }}
      />

      <div className={`${isDefault ? 'disabled-field-container' : ''}`}>
        <TextInput
          placeholder="e.g entertainment"
          title="Theme Name"
          value={name}
          validatior={() => true}
          error={showNameErrorMessage}
          maxLength={65}
          isDefaultValue={name === null}
          onChange={(newName: string) => localThemeChanged({ name: newName })}
        />
        <div className={`text-field-error ${showNameErrorMessage ? 'visible' : ''}`}>
          {isNameEmpty && <span>Please insert a theme name</span>}
          {isThemeNameTaken && <span>Theme name already exists</span>}
        </div>
        <SpacerVertical size="20px" />
      </div>

      <div className={`${isDefault ? 'disabled-field-container' : ''}`}>
        <SelectInput
          title={<ImplementationTitle />}
          onChange={(option: { value: ImplementationMethod }) =>
            localThemeChanged({ implementation_method: option.value })
          }
          options={IMPLEMENTATIONS_OPTIONS}
          value={IMPLEMENTATIONS_OPTIONS.find(option => implementationMethod === option.value)}
        />
        {implementationMethod === 'manual' && <ManualEmbedCode spotId={spotId} themeName={name} />}
        {showRemoveCode && <RemoveEmbedCodeMessage />}
      </div>

      {!isDefault && implementationMethod === 'keywords' && (
        <Fragment>
          <SpacerVertical size="20px" />
          <TextInput
            placeholder="e.g jeopardy,alex trebek,nbc"
            title={<KeywordsTitle />}
            value={keywords}
            validatior={() => true}
            error={showKeywordsErrorMessage}
            isDefaultValue={keywords === null}
            onChange={(newKeywords: string) => localThemeChanged({ keywords: newKeywords })}
          />
          <div className={`text-field-error ${showKeywordsErrorMessage ? 'visible' : ''}`}>
            Please insert a keyword
          </div>
        </Fragment>
      )}

      <div className="modal-fields-seperator" />

      <Fragment>
        <TextInput
          title="Reactions Title"
          value={title === null ? DEFAULT_TITLE : title}
          validatior={() => true}
          error={showTitleErrorMessage}
          maxLength={65}
          isDefaultValue={title === null}
          onChange={(newTitle: string) => localThemeChanged({ title: newTitle })}
        />
        <div className="reactions-title-footer-actions">
          <div className={`text-field-error ${showTitleErrorMessage ? 'visible' : ''}`}>
            Please insert a reactions title
          </div>
          <button
            className={`reaction-reset-reaction-name-action btn-link ${
              title === null ? 'is-hidden' : ''
            }`}
            onClick={() => localThemeChanged({ title: null })}
          >
            Reset to Default
          </button>
        </div>
      </Fragment>

      <SpacerVertical size="20px" />

      <section className="extended-settings-container">
        <div className="field">
          <div className="field-title">Icons & Labels</div>
          <div className="reaction-sub-header">
            Please select {minReactions} to {maxReactions} reactions and a square aspect ratio for
            images/gIfs, up to 100kb
          </div>
          <SortableReactionsContainer onSortEnd={reactionOrderChanged} useDragHandle lockAxis="y">
            {(reactions || [])
              .sort((a, b) => (b.pos > a.pos ? -1 : 1))
              .map((reaction, index) => {
                return (
                  <SortableReaction
                    key={reaction.id}
                    index={index}
                    reaction={reaction}
                    onSettingsChange={reactionSettingsChanged}
                    spotId={spotId}
                  />
                );
              })}
          </SortableReactionsContainer>
        </div>
        <div
          className={`text-field-error ${showActiveReactionsCountErrorMessage ? 'visible' : ''}`}
        >
          You must select at least {minReactions} reactions
        </div>
      </section>

      <SpacerVertical size="25px" />

      <section className="footer">
        <button className="policy-button" onClick={onSave}>
          Done
        </button>

        <button className="cancel-button" onClick={onClose}>
          Cancel
        </button>
      </section>
    </div>
  );
};

export default ThemeModal;
